/**
 * vm.js — winkel-extra's bovenop Horizon (Viola Mia / Oliva Blanca / Livia Hart).
 *
 * - <vm-card-buy>: keuzelijsten + "Aggiungi al carrello" op productkaarten
 * - Bezorgbescherming: standaard aan, max. 1x, uit = opt-out voor deze sessie,
 *   verdwijnt vanzelf als er geen echte producten meer in de winkelwagen zitten
 * - Upsell in de winkelwagen + pop-up voor maat/kleur
 * - Zwevende winkelwagen-knop (aantal zonder bezorgbescherming)
 * - Productpagina: variant-afhankelijke blokken verversen, voorraad-teller
 * - Eenvoudige carrousel-pijltjes voor eigen secties
 *
 * Alle winkelwagen-wijzigingen sturen Horizon's CartLinesUpdateEvent uit,
 * zodat de drawer, de teller in de header en andere onderdelen meelopen.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const VM = (window.VM = window.VM || {});
const cfg = VM.config || {};
const texts = cfg.texts || {};
const PROTECTION_VARIANT = cfg.protectionVariantId ? String(cfg.protectionVariantId) : '';
const PROTECTION_PRODUCT = cfg.protectionProductId ? String(cfg.protectionProductId) : '';
const OPTOUT_KEY = 'vm_shipping_protection_optout';

/* ------------------------------------------------------------------ */
/* Geld                                                                */
/* ------------------------------------------------------------------ */

function formatWithDelimiters(cents, precision, thousands, decimal) {
  const value = (cents / 100).toFixed(precision);
  const [whole, fraction] = value.split('.');
  const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
}

export function formatMoney(cents) {
  const format = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
  const amount = Number(cents) || 0;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount':
        return formatWithDelimiters(amount, 2, ',', '.');
      case 'amount_no_decimals':
        return formatWithDelimiters(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return formatWithDelimiters(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatWithDelimiters(amount, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return formatWithDelimiters(amount, 2, "'", '.');
      case 'amount_with_space_separator':
        return formatWithDelimiters(amount, 2, ' ', ',');
      default:
        return formatWithDelimiters(amount, 2, '.', ',');
    }
  });
}

/* ------------------------------------------------------------------ */
/* Winkelwagen                                                         */
/* ------------------------------------------------------------------ */

const routes = {
  cart: cfg.cartUrl || '/cart',
  add: cfg.cartAddUrl || '/cart/add',
  change: cfg.cartChangeUrl || '/cart/change',
};

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  });
  return [...ids];
}

async function getCart() {
  const response = await fetch(`${routes.cart}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

function isProtectionItem(item) {
  if (!item) return false;
  if (PROTECTION_PRODUCT && String(item.product_id) === PROTECTION_PRODUCT) return true;
  return PROTECTION_VARIANT !== '' && String(item.variant_id) === PROTECTION_VARIANT;
}

function protectionQuantity(cart) {
  return (cart?.items || []).reduce((total, item) => (isProtectionItem(item) ? total + item.quantity : total), 0);
}

function realQuantity(cart) {
  return (cart?.items || []).reduce((total, item) => (isProtectionItem(item) ? total : total + item.quantity), 0);
}

VM.protectionQty = Number(cfg.protectionQty) || 0;
VM.visibleCount = (raw) => Math.max(0, (Number(raw) || 0) - (VM.protectionQty || 0));
VM.countFromAjaxCart = (cart) => realQuantity(cart);

function rememberCart(cart) {
  VM.protectionQty = protectionQuantity(cart);
  VM.lastCart = cart;
  updateFloatingCount(realQuantity(cart));
}

/**
 * Voert een winkelwagen-wijziging uit en laat Horizon meelopen.
 * @param {object} options
 * @param {'add'|'update'|'remove'} options.action
 * @param {'add'|'change'} options.endpoint
 * @param {object} options.body
 * @param {Array<{merchandiseId: string, quantity: number}>} options.lines
 * @param {EventTarget} [options.source]
 * @param {'product'|'cart'|'dialog'} [options.context]
 */
async function mutateCart({ action, endpoint, body, lines, source = document, context = 'cart' }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context,
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const sections = cartSectionIds();
    const response = await fetch(`${endpoint === 'add' ? routes.add : routes.change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...body, sections: sections.join(',') }),
    });
    const json = await response.json();

    if (json.status || !response.ok) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: json.message || json.description || 'Cart update failed',
          code: 'INVALID',
        })
      );
      const cart = await getCart();
      rememberCart(cart);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'vm', itemCount: cart.item_count },
      });
      return { ok: false, message: json.description || json.message, cart };
    }

    const cart = await getCart();
    rememberCart(cart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        sections: json.sections,
        source: 'vm',
        itemCount: cart.item_count,
      },
    });
    return { ok: true, cart };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, error };
  }
}

VM.addToCart = (variantId, quantity = 1, source = document) =>
  mutateCart({
    action: 'add',
    endpoint: 'add',
    context: 'product',
    source,
    body: { items: [{ id: Number(variantId), quantity }] },
    lines: [{ merchandiseId: String(variantId), quantity }],
  });

/* ------------------------------------------------------------------ */
/* Bezorgbescherming                                                   */
/* ------------------------------------------------------------------ */

function optedOut() {
  try {
    return sessionStorage.getItem(OPTOUT_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function setOptOut(value) {
  try {
    if (value) sessionStorage.setItem(OPTOUT_KEY, '1');
    else sessionStorage.removeItem(OPTOUT_KEY);
  } catch (_) {
    /* opslag geblokkeerd: niets aan te doen */
  }
}

let protectionBusy = false;
let protectionPending = false;

async function setProtection(quantity, cart) {
  if (!PROTECTION_VARIANT) return;
  const current = protectionQuantity(cart);
  if (current === quantity) return;

  protectionBusy = true;
  try {
    if (current === 0 && quantity > 0) {
      await mutateCart({
        action: 'update',
        endpoint: 'add',
        body: { items: [{ id: Number(PROTECTION_VARIANT), quantity }] },
        lines: [{ merchandiseId: PROTECTION_VARIANT, quantity }],
      });
    } else {
      const line = (cart?.items || []).find(isProtectionItem);
      await mutateCart({
        action: quantity === 0 ? 'remove' : 'update',
        endpoint: 'change',
        body: line ? { id: line.key, quantity } : { id: PROTECTION_VARIANT, quantity },
        lines: [{ merchandiseId: PROTECTION_VARIANT, quantity }],
      });
    }
  } finally {
    protectionBusy = false;
  }
}

async function syncProtection() {
  if (!PROTECTION_VARIANT) return;
  if (protectionBusy) {
    protectionPending = true;
    return;
  }
  protectionBusy = true;
  try {
    const cart = await getCart();
    rememberCart(cart);
    const real = realQuantity(cart);
    const protection = protectionQuantity(cart);
    protectionBusy = false;

    if (real === 0) {
      setOptOut(false);
      if (protection > 0) await setProtection(0, cart);
    } else if (protection > 1) {
      await setProtection(1, cart);
    } else if (protection === 0 && cfg.protectionDefaultOn && !optedOut()) {
      await setProtection(1, cart);
    }
  } catch (error) {
    console.warn('[vm] protection sync failed', error);
  } finally {
    protectionBusy = false;
    if (protectionPending) {
      protectionPending = false;
      setTimeout(syncProtection, 60);
    }
  }
}

document.addEventListener('change', async (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-vm-protection-toggle]') : null;
  if (!toggle) return;
  const checked = /** @type {HTMLInputElement} */ (toggle).checked;
  setOptOut(!checked);
  try {
    const cart = await getCart();
    await setProtection(checked ? 1 : 0, cart);
  } catch (error) {
    console.warn('[vm] protection toggle failed', error);
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!PROTECTION_VARIANT) return;
  if (protectionBusy) {
    protectionPending = true;
    return;
  }
  event.promise
    ?.then(() => setTimeout(syncProtection, 40))
    .catch(() => {});
});

/* ------------------------------------------------------------------ */
/* Zwevende winkelwagen-knop                                           */
/* ------------------------------------------------------------------ */

function updateFloatingCount(count) {
  document.querySelectorAll('[data-vm-floating-count]').forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart }) => {
      if (!cart) return;
      updateFloatingCount(VM.visibleCount(cart.totalQuantity));
    })
    .catch(() => {});
});

/* ------------------------------------------------------------------ */
/* Productkaart: keuzelijsten + knop                                    */
/* ------------------------------------------------------------------ */

class VMCardBuy extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    try {
      this.variants = JSON.parse(this.querySelector('[data-vm-variants]')?.textContent || '[]');
    } catch (_) {
      this.variants = [];
    }
    this.selects = [...this.querySelectorAll('select.vm-card-buy__select')];
    this.button = this.querySelector('[data-vm-card-add]');
    this.label = this.querySelector('[data-vm-label]');

    this.selects.forEach((select) => {
      [...select.options].forEach((option) => {
        option.dataset.value = option.value;
      });
    });

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) this.update();
    });
    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.add();
    });

    this.update();
  }

  selection() {
    return this.selects.map((select) => select.value);
  }

  findVariant(selection = this.selection()) {
    if (!this.selects.length) return this.variants[0] || null;
    return this.variants.find((variant) => variant.options.every((value, index) => value === selection[index])) || null;
  }

  valueAvailable(index, value, selection) {
    return this.variants.some(
      (variant) =>
        variant.available &&
        variant.options[index] === value &&
        variant.options.every((optionValue, i) => i === index || !selection[i] || optionValue === selection[i])
    );
  }

  update() {
    const selection = this.selection();

    this.selects.forEach((select, index) => {
      [...select.options].forEach((option) => {
        const value = option.dataset.value || option.value;
        const available = this.valueAvailable(index, value, selection);
        const text = available ? value : `${value} – ${texts.soldOut || ''}`;
        if (option.textContent !== text) option.textContent = text;
      });
    });

    const variant = this.findVariant(selection);
    this.variant = variant;

    if (this.button && this.label) {
      if (!variant) {
        this.button.disabled = true;
        this.label.textContent = texts.unavailable || '';
      } else if (!variant.available) {
        this.button.disabled = true;
        this.label.textContent = texts.soldOut || '';
      } else {
        this.button.disabled = false;
        this.label.textContent = texts.add || '';
      }
      if (variant) this.button.dataset.variantId = String(variant.id);
    }

    this.updatePrice(variant);
  }

  updatePrice(variant) {
    if (!variant) return;
    const card = this.closest('product-card, .vm-product-card, li, .product-card__content');
    const price = card?.querySelector('[data-vm-card-price]');
    if (!price) return;

    const compare = price.querySelector('[data-vm-compare]');
    const current = price.querySelector('[data-vm-current]');
    const status = price.querySelector('[data-vm-status]');
    const onSale = variant.compare > variant.price;

    if (compare) {
      compare.hidden = !onSale;
      if (onSale) compare.textContent = formatMoney(variant.compare);
    }
    if (current) {
      current.textContent = formatMoney(variant.price);
      current.classList.toggle('vm-card-price__current--sale', onSale);
    }
    if (status) {
      if (!variant.available) status.textContent = texts.soldOut || '';
      else status.textContent = onSale ? texts.onSale || '' : '';
    }
  }

  async add() {
    const variant = this.variant || this.findVariant();
    if (!variant || !variant.available || !this.button || this.button.disabled) return;

    this.button.classList.add('is-loading');
    this.button.disabled = true;
    const result = await VM.addToCart(variant.id, 1, this.button);
    this.button.classList.remove('is-loading');
    this.button.disabled = false;

    if (result.ok) {
      this.button.classList.add('is-added');
      setTimeout(() => this.button?.classList.remove('is-added'), 1600);
    } else if (this.label) {
      const original = this.label.textContent;
      this.label.textContent = result.message || texts.unavailable || '';
      setTimeout(() => {
        if (this.label) this.label.textContent = original;
      }, 2500);
    }
  }
}

if (!customElements.get('vm-card-buy')) customElements.define('vm-card-buy', VMCardBuy);

/* ------------------------------------------------------------------ */
/* Upsell in de winkelwagen + pop-up                                    */
/* ------------------------------------------------------------------ */

let quickAdd = null;

function ensureQuickAdd() {
  if (quickAdd) return quickAdd;
  const dialog = document.createElement('dialog');
  dialog.className = 'vm-qa';
  dialog.innerHTML = `
    <div class="vm-qa__panel">
      <button type="button" class="vm-qa__close" data-vm-qa-close aria-label="Close">&times;</button>
      <div class="vm-qa__head">
        <img class="vm-qa__img" data-vm-qa-img alt="" width="100" height="150">
        <div>
          <div class="vm-qa__title" data-vm-qa-title></div>
          <div class="vm-qa__price" data-vm-qa-price></div>
        </div>
      </div>
      <div class="vm-qa__options" data-vm-qa-options></div>
      <button type="button" class="vm-qa__submit" data-vm-qa-submit disabled></button>
      <a class="vm-qa__details" data-vm-qa-details hidden></a>
    </div>`;
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || (event.target instanceof Element && event.target.closest('[data-vm-qa-close]'))) {
      dialog.close();
    }
  });

  quickAdd = { dialog, product: null, selections: [], sync: false };
  dialog.querySelector('[data-vm-qa-submit]')?.addEventListener('click', async (event) => {
    const submit = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const id = submit.dataset.variantId;
    if (!id) return;
    submit.classList.add('is-loading');
    submit.disabled = true;
    const result = await VM.addToCart(id, 1, document);
    submit.classList.remove('is-loading');
    submit.disabled = false;
    if (result.ok) dialog.close();
  });
  return quickAdd;
}

function quickAddValueEnabled(index, value) {
  const { product, selections, sync } = quickAdd;
  const matches = (variant, strict) =>
    variant.available &&
    variant.options[index] === value &&
    (!strict || selections.every((selected, i) => i === index || !selected || variant.options[i] === selected));
  if (product.variants.some((v) => matches(v, true))) return true;
  // Op de productpagina mag je altijd naar een andere kleur/maat die ergens op voorraad is
  return sync && product.variants.some((v) => matches(v, false));
}

/* Keuze in de pop-up doorzetten naar de keuzes op de productpagina (Swatch King of Horizon) */
function syncMainPicker(index, value) {
  const optionKey = `option${index + 1}`;
  const swatch = Array.from(
    document.querySelectorAll(`.swatches-type-products .swatch-selector[swatch-option="${optionKey}"]`)
  ).find((el) => el.getAttribute('data-value') === value);
  if (swatch instanceof HTMLElement) {
    if (!swatch.classList.contains('swatch-selected')) swatch.click();
    return;
  }
  const picker = document.querySelector('.product-details variant-picker, variant-picker');
  if (!picker) return;
  const fieldset = picker.querySelectorAll('fieldset')[index];
  const input = fieldset
    ? Array.from(fieldset.querySelectorAll('input')).find((i) => i.value === value)
    : null;
  if (input instanceof HTMLInputElement && !input.checked) {
    input.click();
    return;
  }
  const select = picker.querySelectorAll('select')[index];
  if (select instanceof HTMLSelectElement) {
    const option = Array.from(select.options).find((o) => o.value === value || o.textContent?.trim() === value);
    if (option && !option.selected) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

function renderQuickAdd() {
  const { dialog, product, selections, sync } = quickAdd;
  const wrap = dialog.querySelector('[data-vm-qa-options]');
  if (!wrap || !product) return;
  wrap.innerHTML = '';

  product.options.forEach((option, index) => {
    const group = document.createElement('div');
    group.className = 'vm-qa__group';
    const label = document.createElement('div');
    label.className = 'vm-qa__label';
    label.textContent = selections[index] ? `${option.name}: ${selections[index]}` : option.name;
    const row = document.createElement('div');
    row.className = 'vm-qa__values';

    option.values.forEach((value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vm-qa__value';
      button.textContent = value;
      if (!quickAddValueEnabled(index, value)) {
        button.disabled = true;
        button.classList.add('is-disabled');
      }
      if (selections[index] === value) button.classList.add('is-selected');
      button.addEventListener('click', () => {
        if (sync) {
          const before = selections.slice();
          selections[index] = value;
          // Combinatie niet leverbaar? Pak de eerste leverbare variant met deze waarde.
          const exact = product.variants.find((v) => v.available && v.options.every((o, i) => o === selections[i]));
          if (!exact) {
            const fallback = product.variants.find((v) => v.available && v.options[index] === value);
            if (fallback) fallback.options.forEach((o, i) => (selections[i] = o));
          }
          selections.forEach((selected, i) => {
            if (selected && selected !== before[i]) syncMainPicker(i, selected);
          });
        } else {
          selections[index] = selections[index] === value ? '' : value;
        }
        renderQuickAdd();
      });
      row.appendChild(button);
    });

    group.append(label, row);
    wrap.appendChild(group);
  });

  const complete = selections.every(Boolean);
  const variant = complete
    ? product.variants.find((v) => v.options.every((value, i) => value === selections[i])) || null
    : null;
  const candidates = product.variants.filter((v) => selections.every((s, i) => !s || v.options[i] === s));
  const preview = variant || candidates.find((v) => v.available && v.image) || candidates.find((v) => v.image) || null;

  const img = /** @type {HTMLImageElement|null} */ (dialog.querySelector('[data-vm-qa-img]'));
  const src = (preview && preview.image) || product.image || '';
  if (img && src && img.getAttribute('src') !== src) img.src = src;

  const price = dialog.querySelector('[data-vm-qa-price]');
  if (price) {
    const shown = variant || candidates.find((v) => v.available) || null;
    price.innerHTML = '';
    const current = document.createElement('span');
    current.className = 'vm-qa__price-current';
    current.textContent = (shown && shown.price) || product.priceFrom || '';
    price.appendChild(current);
    if (shown && shown.compare) {
      const compare = document.createElement('s');
      compare.className = 'vm-qa__price-compare';
      compare.textContent = shown.compare;
      price.appendChild(compare);
      if (shown.pct > 0) {
        const badge = document.createElement('span');
        badge.className = 'vm-qa__price-badge';
        badge.textContent = `-${shown.pct}%`;
        price.appendChild(badge);
      }
    }
  }

  const submit = /** @type {HTMLButtonElement|null} */ (dialog.querySelector('[data-vm-qa-submit]'));
  if (!submit) return;
  if (variant && variant.available) {
    submit.disabled = false;
    submit.textContent = texts.submit || '';
    submit.dataset.variantId = String(variant.id);
  } else {
    submit.disabled = true;
    submit.textContent = variant ? texts.qaSoldOut || texts.soldOut || '' : texts.choose || '';
    delete submit.dataset.variantId;
  }
}

/**
 * @param {any} product - productdata uit vm-product-json
 * @param {{ variantId?: string|number, sync?: boolean, url?: string, preselectColorOnly?: boolean }} [options]
 */
function openQuickAddProduct(product, options = {}) {
  if (!product || !product.options || !product.variants) return false;
  ensureQuickAdd();
  quickAdd.product = product;
  quickAdd.sync = Boolean(options.sync);
  const current = options.variantId
    ? product.variants.find((v) => String(v.id) === String(options.variantId))
    : null;
  const colorName = /^(colou?r|colore|kleur|couleur|farbe)$/i;
  quickAdd.selections = product.options.map((option, index) => {
    if (option.values.length === 1) return option.values[0];
    if (!current) return '';
    // Vanaf een productkaart alleen de kleur voorkiezen, de maat kiest de klant zelf
    if (options.preselectColorOnly && !colorName.test(String(option.name).trim())) return '';
    return current.options[index];
  });
  const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
  product.priceFrom = firstAvailable ? firstAvailable.price : '';

  const title = quickAdd.dialog.querySelector('[data-vm-qa-title]');
  if (title) title.textContent = product.title || '';
  const img = /** @type {HTMLImageElement|null} */ (quickAdd.dialog.querySelector('[data-vm-qa-img]'));
  if (img) {
    img.src = product.image || '';
    img.alt = product.title || '';
  }
  quickAdd.dialog.classList.toggle('vm-qa--sheet', quickAdd.sync);
  const details = /** @type {HTMLAnchorElement|null} */ (quickAdd.dialog.querySelector('[data-vm-qa-details]'));
  if (details) {
    details.hidden = !(options.url && texts.details);
    if (options.url) details.href = options.url;
    details.textContent = texts.details || '';
  }
  renderQuickAdd();
  quickAdd.dialog.showModal();
  return true;
}

function readJson(element) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent || '{}');
  } catch (_) {
    return null;
  }
}

function openQuickAdd(card) {
  return openQuickAddProduct(readJson(card.querySelector('[data-vm-upsell-data]')));
}

/* Sticky balk op de productpagina: maat/kleur aanpassen via de pop-up */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const opener = target?.closest('[data-vm-sticky-open]');
  if (!opener) return;
  event.preventDefault();
  const sticky = opener.closest('sticky-add-to-cart');
  if (!(sticky instanceof HTMLElement)) return;
  openQuickAddProduct(readJson(sticky.querySelector('[data-vm-sticky-data]')), {
    variantId: sticky.dataset.currentVariantId,
    sync: true,
  });
});

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const addButton = target.closest('[data-vm-upsell-add]');
  if (addButton instanceof HTMLButtonElement) {
    event.preventDefault();
    const count = parseInt(addButton.dataset.variantCount || '1', 10);
    const card = addButton.closest('.vm-upsell__card');
    if (count > 1 && card && openQuickAdd(card)) return;
    if (!addButton.dataset.variantId) return;
    addButton.classList.add('is-loading');
    addButton.disabled = true;
    await VM.addToCart(addButton.dataset.variantId, 1, addButton);
    addButton.classList.remove('is-loading');
    addButton.disabled = false;
    return;
  }

  const arrow = target.closest('[data-vm-upsell-prev], [data-vm-upsell-next], [data-vm-scroll-prev], [data-vm-scroll-next]');
  if (arrow) {
    const root = arrow.closest('[data-vm-upsell], [data-vm-scroller]');
    const track = root?.querySelector('[data-vm-upsell-track], [data-vm-scroll-track]');
    if (!(track instanceof HTMLElement)) return;
    const item = track.firstElementChild;
    const step = item instanceof HTMLElement ? item.getBoundingClientRect().width + 12 : track.clientWidth * 0.8;
    const direction = arrow.matches('[data-vm-upsell-prev], [data-vm-scroll-prev]') ? -1 : 1;
    track.scrollBy({ left: direction * step * (window.innerWidth < 750 ? 1 : 2), behavior: 'smooth' });
  }
});

/* ------------------------------------------------------------------ */
/* Productpagina: variant-afhankelijke blokken                          */
/* ------------------------------------------------------------------ */

document.addEventListener(StandardEvents.productSelect, (event) => {
  const origin = event.target instanceof Element ? event.target : null;
  if (!origin || origin.closest('product-card')) return;
  const scope = origin.closest('.shopify-section, dialog') || document;

  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html || typeof html.querySelectorAll !== 'function') return;
      scope.querySelectorAll('[data-vm-refresh]').forEach((element) => {
        const key = element.getAttribute('data-vm-refresh');
        if (!key) return;
        const replacement = html.querySelector(`[data-vm-refresh="${CSS.escape(key)}"]`);
        if (replacement) element.innerHTML = replacement.innerHTML;
        element.dispatchEvent(new CustomEvent('vm:refreshed', { bubbles: true }));
      });
      initStockCounters(scope);
    })
    .catch(() => {});
});

/* Voorraad-teller (8 → 7 → 6 → 5) op de productpagina */
function initStockCounters(root = document) {
  root.querySelectorAll('[data-vm-stock]').forEach((stock) => {
    if (!(stock instanceof HTMLElement)) return;
    const num = stock.querySelector('[data-vm-stock-num]');
    const fill = stock.querySelector('[data-vm-stock-fill]');
    if (!num || !(fill instanceof HTMLElement)) return;

    const steps = (stock.dataset.steps || '8,7,6,5').split(',').map((n) => parseInt(n, 10)).filter((n) => n > 0);
    const delays = [0, 2000, 6000, 12000];
    const started = Number(window.VM._stockStart || 0) || Date.now();
    window.VM._stockStart = started;

    const paint = () => {
      const elapsed = Date.now() - started;
      let value = steps[0] || 8;
      steps.forEach((step, index) => {
        if (elapsed >= (delays[index] ?? delays[delays.length - 1])) value = step;
      });
      num.textContent = String(value);
      fill.style.width = `${Math.min(100, value * 9)}%`;
    };

    paint();
    if (!stock.dataset.vmTicking) {
      stock.dataset.vmTicking = 'true';
      delays.slice(1).forEach((delay) => setTimeout(paint, delay - (Date.now() - started) + 20));
    }
  });
}

/* ------------------------------------------------------------------ */
/* Start                                                               */
/* ------------------------------------------------------------------ */

function start() {
  initStockCounters(document);
  if (PROTECTION_VARIANT && Number(cfg.cartCount) > 0) setTimeout(syncProtection, 400);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

/* Maattabel-pop-up op de productpagina */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const opener = target.closest('[data-vm-size-guide-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-vm-size-guide-open') || '');
    if (dialog instanceof HTMLDialogElement) dialog.showModal();
    return;
  }
  const dialog = target.closest('.vm-size-guide__dialog');
  if (dialog instanceof HTMLDialogElement && (target === dialog || target.closest('[data-vm-size-guide-close]'))) {
    dialog.close();
  }
});

/* Editor: sectie "Winkelwagen" aanklikken opent de winkelwagen in de preview */
if (window.Shopify && window.Shopify.designMode) {
  let cartSectionSelected = false;
  const isCartSection = (event) => event.target instanceof Element && Boolean(event.target.querySelector('#cart-drawer'));
  const drawer = () => /** @type {any} */ (document.getElementById('cart-drawer'));
  const openDrawer = () => setTimeout(() => drawer()?.open?.(), 150);

  document.addEventListener('shopify:section:select', (event) => {
    if (!isCartSection(event)) return;
    cartSectionSelected = true;
    openDrawer();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (!isCartSection(event)) return;
    cartSectionSelected = false;
    drawer()?.close?.();
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (cartSectionSelected && isCartSection(event)) openDrawer();
  });
}


/* ------------------------------------------------------------------ */
/* Productkaart: kleurkeuze-blokje (vm-card-color)                      */
/* ------------------------------------------------------------------ */

function closeColorLists(except) {
  document.querySelectorAll('[data-vm-cc].is-open').forEach((cc) => {
    if (cc === except) return;
    cc.classList.remove('is-open');
    const list = cc.querySelector('.vm-cc__list');
    if (list instanceof HTMLElement) list.hidden = true;
    cc.querySelector('.vm-cc__toggle')?.setAttribute('aria-expanded', 'false');
  });
}

function showCardMedia(card, mediaId) {
  if (!card || !mediaId) return;
  const slideshow = /** @type {any} */ (card.querySelector('slideshow-component'));
  if (!slideshow) return;
  const slides = slideshow.refs?.slides || [...slideshow.querySelectorAll('slideshow-slide')];
  let found = false;
  for (const slide of slides) {
    if (slide.getAttribute('slide-id') === String(mediaId)) found = true;
    if (slide.getAttribute('variant-image') == null) continue;
    slide.hidden = slide.getAttribute('slide-id') !== String(mediaId);
  }
  if (found && typeof slideshow.select === 'function') {
    slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
  }
}

function setCardVariant(card, variantId) {
  if (!card || !variantId) return;
  card.querySelectorAll('a[href]').forEach((link) => {
    try {
      const url = new URL(link.getAttribute('href') || '', window.location.origin);
      if (!url.pathname.includes('/products/')) return;
      url.searchParams.set('variant', String(variantId));
      link.setAttribute('href', url.pathname + url.search);
    } catch (_) {}
  });
  card.querySelectorAll('[data-vm-cqa]').forEach((button) => {
    if (button instanceof HTMLElement) button.dataset.variantId = String(variantId);
  });
}

['pointerdown', 'touchstart', 'mousedown'].forEach((type) => {
  document.addEventListener(
    type,
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-vm-cc], [data-vm-cqa]')) event.stopPropagation();
    },
    { capture: true, passive: true }
  );
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('.vm-cc--choice .vm-cc__toggle');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const cc = toggle.closest('[data-vm-cc]');
      if (!cc) return;
      const open = !cc.classList.contains('is-open');
      closeColorLists(cc);
      cc.classList.toggle('is-open', open);
      const list = cc.querySelector('.vm-cc__list');
      if (list instanceof HTMLElement) list.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const option = target.closest('.vm-cc__opt');
    if (option instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      const cc = option.closest('[data-vm-cc]');
      if (!cc) return;
      cc.querySelectorAll('.vm-cc__opt').forEach((opt) => {
        const on = opt === option;
        opt.classList.toggle('is-selected', on);
        opt.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const label = cc.querySelector('[data-vm-cc-label]');
      if (label) label.textContent = option.dataset.value || '';
      const dot = cc.querySelector('[data-vm-cc-dot]');
      const optionDot = option.querySelector('.vm-cc__dot');
      if (dot && optionDot) dot.setAttribute('style', optionDot.getAttribute('style') || '');
      closeColorLists(null);
      const card = option.closest('product-card');
      showCardMedia(card, option.dataset.media);
      setCardVariant(card, option.dataset.variant);
      return;
    }

    if (!target.closest('[data-vm-cc]')) closeColorLists(null);
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeColorLists(null);
});

/* ------------------------------------------------------------------ */
/* Productkaart: snel-toevoegen knop op de foto                         */
/* ------------------------------------------------------------------ */

const productCache = new Map();

async function loadProduct(handle) {
  if (productCache.has(handle)) return productCache.get(handle);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('product');
  const raw = await response.json();
  const imageUrl = (src) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=300` : '');
  const product = {
    id: raw.id,
    title: raw.title,
    image: imageUrl(raw.featured_image || ''),
    options: (raw.options || []).map((option) => ({ name: option.name, values: option.values })),
    variants: (raw.variants || []).map((variant) => {
      const pct =
        variant.compare_at_price > variant.price
          ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
          : 0;
      return {
        id: variant.id,
        available: variant.available,
        options: variant.options,
        price: formatMoney(variant.price),
        compare: variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '',
        pct,
        image: imageUrl(variant.featured_image?.src || ''),
      };
    }),
  };
  productCache.set(handle, product);
  return product;
}

document.addEventListener(
  'click',
  async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-vm-cqa]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    try {
      const product = await loadProduct(button.dataset.handle || '');
      const preferred = button.dataset.variantId;
      if (product.variants.length === 1) {
        const result = await VM.addToCart(product.variants[0].id, 1, button);
        if (result.ok) {
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 1600);
        }
      } else {
        let url = button.dataset.url || '';
        if (preferred && url) url += `${url.includes('?') ? '&' : '?'}variant=${preferred}`;
        openQuickAddProduct(product, { variantId: preferred, url, preselectColorOnly: true });
      }
    } catch (_) {
      if (button.dataset.url) window.location.href = button.dataset.url;
    } finally {
      button.classList.remove('is-loading');
    }
  },
  true
);
